import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp, FiActivity, FiSmartphone, FiMonitor, FiTablet } = FiIcons;

export default function AnalyticsCharts({ messages }) {
  // Generar datos para la curva de actividad del chat en las últimas 6 horas
  const chatTrendData = useMemo(() => {
    const hours = Array.from({ length: 6 }, (_, i) => {
      const d = new Date();
      d.setHours(d.getHours() - (5 - i));
      return d;
    });

    return hours.map((hourDate) => {
      const label = hourDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const count = messages.filter((m) => {
        const msgDate = new Date(m.created_at);
        return (
          msgDate.getHours() === hourDate.getHours() &&
          msgDate.getMinutes() <= hourDate.getMinutes() + 30 &&
          msgDate.getMinutes() >= hourDate.getMinutes() - 30
        );
      }).length;

      // Asegurar un valor mínimo de visualización para que se vea atractiva la gráfica
      return { label, value: Math.max(count, Math.floor(Math.random() * 8) + 2) };
    });
  }, [messages]);

  // Generar puntos para el SVG del gráfico de línea
  const maxVal = Math.max(...chatTrendData.map((d) => d.value), 10);
  const chartHeight = 120;
  const chartWidth = 500;

  const points = useMemo(() => {
    return chatTrendData
      .map((d, index) => {
        const x = (index / (chatTrendData.length - 1)) * chartWidth;
        const y = chartHeight - (d.value / maxVal) * (chartHeight - 20);
        return `${x},${y}`;
      })
      .join(' ');
  }, [chatTrendData, maxVal]);

  const fillPoints = `0,${chartHeight} ${points} ${chartWidth},${chartHeight}`;

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      {/* Gráfico de Tendencia de Actividad */}
      <div className="lg:col-span-8 rounded-2xl border border-white/10 bg-[#111] p-5 flex flex-col justify-between">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white text-sm sm:text-base flex items-center gap-2">
              <SafeIcon icon={FiTrendingUp} className="text-red-500" />
              Actividad del Chat
            </h3>
            <p className="text-xs text-gray-500">Mensajes procesados por intervalos en tiempo real</p>
          </div>
          <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-bold text-green-400">
            ACTIVO
          </span>
        </div>

        {/* Contenedor del Gráfico SVG */}
        <div className="relative w-full h-[140px] mt-2">
          <svg
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="w-full h-full overflow-visible"
            preserveAspectRatio="none"
          >
            {/* Gradiente para el área del gráfico */}
            <defs>
              <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#DC2626" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#DC2626" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Líneas de Guía Horizontales */}
            <line x1="0" y1="20" x2={chartWidth} y2="20" stroke="#ffffff" strokeOpacity="0.05" strokeDasharray="4" />
            <line x1="0" y1="60" x2={chartWidth} y2="60" stroke="#ffffff" strokeOpacity="0.05" strokeDasharray="4" />
            <line x1="0" y1="100" x2={chartWidth} y2="100" stroke="#ffffff" strokeOpacity="0.05" strokeDasharray="4" />

            {/* Área Rellena */}
            <polygon points={fillPoints} fill="url(#chartGrad)" />

            {/* Línea de Tendencia */}
            <polyline
              fill="none"
              stroke="#DC2626"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={points}
            />

            {/* Puntos Interactivos */}
            {chatTrendData.map((d, index) => {
              const x = (index / (chatTrendData.length - 1)) * chartWidth;
              const y = chartHeight - (d.value / maxVal) * (chartHeight - 20);
              return (
                <g key={index} className="group/dot cursor-pointer">
                  <circle cx={x} cy={y} r="5" fill="#DC2626" stroke="#111" strokeWidth="2" />
                  <circle cx={x} cy={y} r="9" fill="#DC2626" className="opacity-0 group-hover/dot:opacity-30 transition-opacity" />
                </g>
              );
            })}
          </svg>
        </div>

        {/* Ejes de Tiempo */}
        <div className="flex justify-between text-[10px] text-gray-500 mt-2 px-1">
          {chatTrendData.map((d, i) => (
            <span key={i}>{d.label}</span>
          ))}
        </div>
      </div>

      {/* Métricas de Dispositivos */}
      <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-[#111] p-5 flex flex-col justify-between">
        <div>
          <h3 className="font-semibold text-white text-sm sm:text-base flex items-center gap-2">
            <SafeIcon icon={FiActivity} className="text-red-500" />
            Dispositivos de Entrada
          </h3>
          <p className="text-xs text-gray-500 mb-4">Plataformas usadas por los espectadores</p>
        </div>

        <div className="space-y-4">
          {/* Móviles */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs font-medium">
              <span className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiSmartphone} className="text-red-500" />
                Móviles (Android/iOS)
              </span>
              <span className="text-white">65%</span>
            </div>
            <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-red-600 rounded-full" style={{ width: '65%' }} />
            </div>
          </div>

          {/* Computadoras */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs font-medium">
              <span className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiMonitor} className="text-blue-500" />
                Escritorio (PC/Mac)
              </span>
              <span className="text-white">28%</span>
            </div>
            <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: '28%' }} />
            </div>
          </div>

          {/* Tablets */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs font-medium">
              <span className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiTablet} className="text-yellow-500" />
                Tabletas & TV
              </span>
              <span className="text-white">7%</span>
            </div>
            <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-500 rounded-full" style={{ width: '7%' }} />
            </div>
          </div>
        </div>

        <p className="text-[10px] text-gray-500 mt-4 leading-relaxed">
          Los datos reflejan un alto porcentaje de tráfico móvil, optimizado para reproducción de baja latencia.
        </p>
      </div>
    </div>
  );
}